import type { ReactNode } from 'react';
import FAIcon from '~/components/utils/FAIcons/FAIcon';
import { useApproval } from '~/hooks/useApproval';

interface IRequestButton {
	askToDoMessage?: string;
	children?: ReactNode;
	className?: string;
	disabled?: boolean;
	disableOnSuccess?: boolean;
	form?: string;
	id?: string;
	isLoading?: boolean;
	isSuccess?: boolean;
	loadingText?: string;
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	showLoadingText?: boolean;
	successText?: string;
	title?: string;
	type?: 'submit' | 'button' | 'reset' | undefined;
	useApproval?: boolean;
}

export const RequestButton = ({
	askToDoMessage = 'Are you sure?',
	children = 'Submit',
	className = 'bg-black px-5 py-2 rounded-md text-white text-lg font-medium',
	disabled = false,
	disableOnSuccess = false,
	form,
	id,
	isLoading = false,
	isSuccess = false,
	loadingText = 'Loading',
	onClick,
	showLoadingText = true,
	successText = 'Success!',
	title,
	type = 'submit',
	useApproval: useApprovalCheck,
}: IRequestButton) => {
	const { askToDo } = useApproval({ defaultMessage: askToDoMessage });

	const handleClick =
		useApprovalCheck && onClick
			? (event: React.MouseEvent<HTMLButtonElement>) => {
					event.preventDefault();
					askToDo(() => onClick(event));
				}
			: onClick;

	function shouldThisBeDisabled() {
		if (disabled) return disabled;
		if (disableOnSuccess && !isLoading) {
			return isSuccess;
		} else {
			return isLoading ? isLoading : disabled;
		}
	}

	const formattedClassName = `${className} ${shouldThisBeDisabled() ? 'opacity-50 cursor-not-allowed' : ''}`;

	return (
		<button
			data-testid={id}
			id={id}
			form={form}
			type={type}
			className={formattedClassName}
			disabled={shouldThisBeDisabled()}
			onClick={handleClick}
			title={title}
		>
			{isLoading ? (
				<div className="flex items-center px-2">
					<FAIcon icon="spinner fa-spin" className={showLoadingText ? 'text-md mr-2' : 'text-sm'} />
					{showLoadingText && <span>{loadingText}</span>}
				</div>
			) : disableOnSuccess && isSuccess && !!successText ? (
				successText
			) : (
				children
			)}
		</button>
	);
};
